import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import FrontechLogo from './FrontechLogo';
import LandingDemo from './LandingDemo';
import Seo from './marketing/Seo';
import AgencyNav from './marketing/AgencyNav';
import {
  Mic, Zap, BarChart3, Code2, ArrowRight, Shield,
  Globe, Headphones, MessageSquare, Users, Bot,
  Sun, Moon, ChevronDown, Play, CheckCircle2, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Animated stat counter ── */
function AnimatedNumber({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current && target > 0) {
        started.current = true;
        const duration = 1800, steps = 45, inc = target / steps;
        let cur = 0;
        const t = setInterval(() => {
          cur += inc;
          if (cur >= target) { setCount(target); clearInterval(t); }
          else setCount(Math.floor(cur));
        }, duration / steps);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref} className="stat-number">{count.toLocaleString()}{suffix}</span>;
}

/* ── Floating constellation background ── */
function ConstellationBg() {
  const dots = [
    { x: '8%',  y: '18%', size: 2.5,  delay: 0 },
    { x: '22%', y: '65%', size: 1.5,  delay: 0.8 },
    { x: '38%', y: '12%', size: 2,    delay: 1.4 },
    { x: '55%', y: '80%', size: 1.8,  delay: 0.3 },
    { x: '72%', y: '28%', size: 3,    delay: 1.1 },
    { x: '85%', y: '55%', size: 1.5,  delay: 0.6 },
    { x: '92%', y: '15%', size: 2,    delay: 1.8 },
    { x: '15%', y: '88%', size: 1.5,  delay: 2.1 },
    { x: '48%', y: '42%', size: 1.8,  delay: 0.5 },
    { x: '63%', y: '70%', size: 2.2,  delay: 1.6 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {dots.map((d, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            left: d.x, top: d.y,
            width: d.size * 2, height: d.size * 2,
            background: 'var(--color-frontech-blue)',
            opacity: 0.25,
            animation: `float ${6 + i * 0.4}s ease-in-out ${d.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

/* ── Feature card ── */
function FeatureCard({ icon: Icon, title, desc, index }) {
  return (
    <div
      data-testid={`feature-card-${index}`}
      className="card-hover-glow relative p-6 rounded-2xl group cursor-default"
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 transition-all duration-300"
        style={{ background: 'var(--color-accent-dim)' }}
      >
        <Icon size={21} style={{ color: 'var(--color-accent)' }} />
      </div>
      <h3 className="font-heading text-base font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
        {title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>{desc}</p>
    </div>
  );
}

/* ── Pricing card ── */
function PricingCard({ name, price, period, subPrice, features, cta, highlight, index, ctaHref = '/signup', isExternal = false, comingSoon = false }) {
  const CtaTag = comingSoon ? 'button' : (isExternal ? 'a' : Link);
  return (
    <div
      data-testid={`pricing-card-${index}`}
      className="relative rounded-2xl p-6 flex flex-col transition-all duration-300 card-hover-glow"
      style={{
        background: 'var(--color-surface)',
        border: highlight ? '1px solid rgba(0,214,126,0.35)' : '1px solid var(--color-border)',
        boxShadow: highlight ? '0 0 40px rgba(0,214,126,0.08)' : 'none',
        opacity: comingSoon ? 0.85 : 1,
      }}
    >
      {highlight && !comingSoon && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="text-xs font-semibold px-3 py-1 rounded-full font-mono tracking-wide whitespace-nowrap"
            style={{ background: 'var(--color-accent)', color: '#000' }}>
            Most Popular
          </span>
        </div>
      )}
      {comingSoon && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="text-xs font-semibold px-3 py-1 rounded-full font-mono tracking-wide whitespace-nowrap"
            style={{ background: 'var(--color-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
            Coming soon
          </span>
        </div>
      )}
      <div className="mb-5">
        <h3 className="font-heading text-base font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>{name}</h3>
        <div className="flex items-baseline gap-1 mt-2 flex-wrap">
          <span className="font-heading text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{price}</span>
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{period}</span>
        </div>
        {subPrice && (
          <p className="text-[11px] mt-1" style={{ color: 'var(--color-text-muted)' }}>{subPrice}</p>
        )}
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {features.map((f, j) => (
          <li key={j} className="flex items-start gap-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            <CheckCircle2 size={13} style={{ color: comingSoon ? 'var(--color-text-muted)' : 'var(--color-accent)', flexShrink: 0, marginTop: 2 }} />
            {f}
          </li>
        ))}
      </ul>
      {comingSoon ? (
        <button
          type="button"
          disabled
          className="block w-full text-center py-2.5 rounded-xl font-semibold text-sm"
          style={{
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-muted)',
            background: 'var(--color-elevated)',
            cursor: 'not-allowed',
          }}
        >
          {cta}
        </button>
      ) : (
        <CtaTag
          {...(isExternal ? { href: ctaHref } : { to: ctaHref })}
          className="block text-center py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer"
          style={highlight ? {
            background: 'var(--color-accent)',
            color: '#000',
          } : {
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
            background: 'transparent',
          }}
        >
          {cta}
        </CtaTag>
      )}
    </div>
  );
}

const STATS_CACHE_KEY = 'landing.demo.stats.v1';
const STATS_BASELINE = { total_conversations: 1247, total_messages: 8934, total_agents: 83 };

function loadInitialStats() {
  try {
    const cached = localStorage.getItem(STATS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed.total_conversations === 'number') return parsed;
    }
  } catch {}
  return STATS_BASELINE;
}

export default function Landing() {
  const { theme, toggle } = useTheme();
  const [stats, setStats] = useState(loadInitialStats);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' | 'yearly'

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API}/api/demo/stats`);
        const data = await res.json();
        const next = {
          total_conversations: data.total_conversations + 1247,
          total_messages: data.total_messages + 8934,
          total_agents: data.total_agents + 83,
        };
        setStats(next);
        try { localStorage.setItem(STATS_CACHE_KEY, JSON.stringify(next)); } catch {}
      } catch {}
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    { icon: Mic,    title: 'Natural Conversations',  desc: 'Real-time speech-to-text and text-to-speech. Conversations that feel human, respond in milliseconds.' },
    { icon: Code2,  title: 'One-Line Embed',          desc: 'Copy a single script tag, paste into any website. Your voice agent is live in under 60 seconds.' },
    { icon: BarChart3, title: 'Live Analytics',       desc: 'Track conversations, duration, satisfaction scores. Understand exactly how users interact.' },
    { icon: Shield, title: 'Enterprise Ready',        desc: 'End-to-end encryption, data isolation, and infrastructure built for scale from day one.' },
  ];

  const steps = [
    { step: '01', title: 'Build',  desc: 'Configure your agent personality, knowledge base, and voice in minutes — no code required.', icon: Zap },
    { step: '02', title: 'Embed',  desc: 'Copy the generated script tag and paste it into your website. One line of HTML, that is it.', icon: Globe },
    { step: '03', title: 'Engage', desc: 'Visitors talk to your AI. Every conversation is tracked, analyzed, and continuously improving.', icon: Headphones },
  ];

  const plans = [
    {
      name: 'Free',
      monthlyPrice: '$0', yearlyPrice: '$0',
      monthlySub: '', yearlySub: '',
      features: ['1 agent', '25 voice minutes / mo', '500 chat messages / mo', 'Basic analytics'],
      cta: 'Start Free', highlight: false,
    },
    {
      name: 'Starter',
      monthlyPrice: '$19', yearlyPrice: '$16',
      monthlySub: '', yearlySub: '$190 billed annually',
      features: ['3 agents', '200 voice minutes / mo', '5,000 chat messages / mo', '1,000 tool calls / mo', 'Email support'],
      cta: 'Coming soon', highlight: false, comingSoon: true,
    },
    {
      name: 'Pro',
      monthlyPrice: '$49', yearlyPrice: '$41',
      monthlySub: '', yearlySub: '$490 billed annually',
      features: ['10 agents', '1,000 voice minutes / mo', '25,000 chat messages / mo', '5,000 tool calls / mo', 'Custom branding', 'Priority support'],
      cta: 'Coming soon', highlight: true, comingSoon: true,
    },
    {
      name: 'Business',
      monthlyPrice: '$149', yearlyPrice: '$124',
      monthlySub: '', yearlySub: '$1,490 billed annually',
      features: ['25 agents', '5,000 voice minutes / mo', '100,000 chat messages / mo', '25,000 tool calls / mo', 'API access', 'Dedicated support'],
      cta: 'Coming soon', highlight: false, comingSoon: true,
    },
    {
      name: 'Enterprise',
      monthlyPrice: 'from $499', yearlyPrice: 'from $416',
      monthlySub: 'Custom contracts', yearlySub: 'Custom contracts',
      features: ['Unlimited agents', 'Unlimited voice & chat', 'White-label', 'Custom integrations', 'SLA & dedicated CSM'],
      cta: 'Coming soon', highlight: false, comingSoon: true,
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: 'var(--color-base)', color: 'var(--color-text-primary)' }}>
      <Seo
        title="Front — AI voice + chat agent for any website · FRONTECH"
        description="Front is an AI support agent that installs on any website with one line of code. Voice + text, tool-calling, live analytics. One-line embed, live in 60 seconds."
        path="/front"
      />

      <AgencyNav />

      {/* ── Hero ── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center pt-14 px-5 pb-16 overflow-hidden">
        {/* Background watermark */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center" aria-hidden="true"
          style={{ opacity: 'var(--watermark-opacity)' }}>
          <FrontechLogo size={600} variant="watermark" color={`rgb(var(--watermark-color))`} />
        </div>

        {/* Constellation dots */}
        <ConstellationBg />

        {/* Gradient blobs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,214,126,0.05) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(91,127,166,0.07) 0%, transparent 70%)', filter: 'blur(60px)' }} />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Logo */}
          <div className="animate-fade-up flex justify-center mb-8">
            <FrontechLogo size={96} variant="animated" color="var(--color-accent)" />
          </div>

          {/* Badge */}
          <div className="animate-fade-up inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-xs font-mono tracking-[0.15em] uppercase"
            style={{
              border: '1px solid rgba(0,214,126,0.2)',
              background: 'rgba(0,214,126,0.05)',
              color: 'var(--color-accent)',
            }}>
            <Sparkles size={12} />
            Conversational AI Infrastructure
          </div>

          {/* Heading */}
          <h1
            data-testid="hero-heading"
            className="font-heading font-bold tracking-tighter leading-[1.04] animate-fade-up-delay-1"
            style={{ fontSize: 'clamp(2.8rem, 7vw, 5rem)', color: 'var(--color-text-primary)' }}
          >
            AI agents that talk, chat,<br />
            <span className="text-gradient-accent">and take action.</span>
          </h1>

          <p className="mt-6 text-lg leading-relaxed max-w-2xl mx-auto animate-fade-up-delay-2"
            style={{ color: 'var(--color-text-secondary)' }}>
            Voice, chat, and tool-using agents — integrable anywhere on the internet<br className="hidden sm:block" />
            or inside your product, grounded in your own knowledge base.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3 animate-fade-up-delay-3">
            <Link
              to="/signup"
              data-testid="hero-cta-btn"
              className="btn-accent text-base px-8 py-3.5"
              style={{ boxShadow: '0 4px 30px rgba(0,214,126,0.2)' }}
            >
              Start free <ArrowRight size={18} />
            </Link>
            <a
              href="#demo"
              className="btn-ghost text-base px-8 py-3.5"
            >
              <Play size={16} /> See live demo
            </a>
          </div>

          {/* Stats */}
          <div
            data-testid="social-proof-counter"
            className="mt-16 grid grid-cols-3 gap-4 sm:gap-8 max-w-lg mx-auto animate-fade-up-delay-4"
          >
            {[
              { icon: MessageSquare, value: stats.total_conversations, label: 'Conversations', suffix: '+' },
              { icon: Bot,           value: stats.total_agents,        label: 'Agents Built',  suffix: '+' },
              { icon: Users,         value: stats.total_messages,      label: 'Messages',      suffix: '+' },
            ].map((s, i) => (
              <div key={i} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`} className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <s.icon size={13} style={{ color: 'var(--color-accent)', opacity: 0.7 }} />
                  <span className="font-heading text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                    <AnimatedNumber target={s.value} suffix={s.suffix} />
                  </span>
                </div>
                <span className="text-xs font-mono uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" style={{ color: 'var(--color-text-muted)' }}>
          <ChevronDown size={20} />
        </div>
      </section>

      {/* ── Live Demo ── */}
      <section id="demo" className="py-24 px-5" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-mono tracking-[0.2em] uppercase px-4 py-2 rounded-full inline-block"
              style={{ color: 'var(--color-accent)', border: '1px solid rgba(0,214,126,0.2)', background: 'var(--color-accent-dim)' }}>
              Try It Live
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-5"
              style={{ color: 'var(--color-text-primary)' }}>
              Experience it right now
            </h2>
            <p className="mt-3 text-base max-w-xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              This is a live Front agent. Type a message or use voice. Every business gets this — in minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-2 py-4">
              <div className="space-y-6">
                {['Real-time AI responses', 'Voice & text support', 'Embeddable anywhere', 'Zero code required'].map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: 'var(--color-accent-dim)' }}>
                      <CheckCircle2 size={14} style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="rounded-2xl overflow-hidden" style={{
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
                boxShadow: '0 20px 80px rgba(0,0,0,0.35)',
              }}>
                <LandingDemo />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-24 px-5" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-mono tracking-[0.2em] uppercase" style={{ color: 'var(--color-accent)' }}>
              Capabilities
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3"
              style={{ color: 'var(--color-text-primary)' }}>
              Everything you need to deploy conversational AI
            </h2>
            <p className="mt-3 text-base max-w-2xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Voice, chat, and tool calls in one stack — embed the widget, hit the REST API, or
              ship inside your mobile app.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => <FeatureCard key={i} {...f} index={i} />)}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-24 px-5" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-mono tracking-[0.2em] uppercase" style={{ color: 'var(--color-accent)' }}>
              Process
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3"
              style={{ color: 'var(--color-text-primary)' }}>
              Three steps to a working agent
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-10 left-1/6 right-1/6 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, var(--color-border), transparent)' }} />
            {steps.map((s, i) => (
              <div key={i} data-testid={`step-card-${i}`} className="text-center relative">
                <span className="text-xs font-mono tracking-widest" style={{ color: 'var(--color-accent)' }}>
                  {s.step}
                </span>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mt-3 mb-5"
                  style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                  <s.icon size={26} style={{ color: 'var(--color-accent)' }} />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                  {s.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-24 px-5" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-mono tracking-[0.2em] uppercase" style={{ color: 'var(--color-accent)' }}>
              Pricing
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3"
              style={{ color: 'var(--color-text-primary)' }}>
              Simple, transparent pricing
            </h2>
            <p className="mt-3 text-base" style={{ color: 'var(--color-text-secondary)' }}>
              Start free. Scale as you grow. No surprises.
            </p>
          </div>

          {/* Monthly / Yearly toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex rounded-lg p-0.5"
              style={{ background: 'var(--color-elevated)', border: '1px solid var(--color-border)' }}>
              <button
                data-testid="billing-toggle-monthly"
                onClick={() => setBillingPeriod('monthly')}
                className="px-4 py-2 text-sm font-semibold rounded-md transition-all"
                style={{
                  background: billingPeriod === 'monthly' ? 'var(--color-accent)' : 'transparent',
                  color: billingPeriod === 'monthly' ? '#000' : 'var(--color-text-secondary)',
                }}>
                Monthly
              </button>
              <button
                data-testid="billing-toggle-yearly"
                onClick={() => setBillingPeriod('yearly')}
                className="px-4 py-2 text-sm font-semibold rounded-md transition-all flex items-center gap-1.5"
                style={{
                  background: billingPeriod === 'yearly' ? 'var(--color-accent)' : 'transparent',
                  color: billingPeriod === 'yearly' ? '#000' : 'var(--color-text-secondary)',
                }}>
                Yearly
                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold"
                  style={{
                    background: billingPeriod === 'yearly' ? '#000' : 'rgba(0,214,126,0.15)',
                    color: 'var(--color-accent)',
                  }}>
                  -17%
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {plans.map((p, i) => {
              const price = billingPeriod === 'yearly' ? p.yearlyPrice : p.monthlyPrice;
              const subPrice = billingPeriod === 'yearly' ? p.yearlySub : p.monthlySub;
              const period = (price === '$0' || (typeof price === 'string' && price.startsWith('from')))
                ? ''
                : '/month';
              return (
                <PricingCard
                  key={i}
                  index={i}
                  name={p.name}
                  price={price}
                  period={period}
                  subPrice={subPrice}
                  features={p.features}
                  cta={p.cta}
                  highlight={p.highlight}
                  ctaHref={p.ctaHref}
                  isExternal={p.isExternal}
                  comingSoon={p.comingSoon}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="py-24 px-5" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="max-w-3xl mx-auto text-center relative">
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-8"
            style={{ opacity: 0.06 }}>
            <FrontechLogo size={300} variant="watermark" color={`rgb(var(--watermark-color))`} />
          </div>
          <div className="relative z-10">
            <FrontechLogo size={52} variant="animated" color="var(--color-accent)" className="mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight"
              style={{ color: 'var(--color-text-primary)' }}>
              Ready to ship an agent<br />
              <span className="text-gradient-accent">that takes action?</span>
            </h2>
            <p className="mt-5 text-lg" style={{ color: 'var(--color-text-secondary)' }}>
              Voice, chat, and tool-using agents your customers can reach<br className="hidden sm:block" />
              on your site, in your app, or anywhere you ship — 24/7.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link to="/signup" className="btn-accent text-base px-9 py-4">
                Start for Free <ArrowRight size={18} />
              </Link>
              <Link to="/login" className="btn-ghost text-base px-9 py-4">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-10 px-5" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <FrontechLogo size={22} variant="icon" color="var(--color-accent)" />
            <span className="font-heading text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Front</span>
            <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>by FRONTECH</span>
          </div>
          <div className="flex items-center gap-5 text-xs">
            <Link to="/privacy" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Privacy</Link>
            <Link to="/terms" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Terms</Link>
            <Link to="/refunds" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Refunds</Link>
            <span style={{ color: 'var(--color-text-muted)' }}>
              &copy; {new Date().getFullYear()} FRONTECH Consulting
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
